import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/* ─── Minimal CSS-in-JS keyframes injector ────────────────────── */
const injectKeyframes = () => {
  if (document.getElementById("karela-keyframes")) return;
  const style = document.createElement("style");
  style.id = "karela-keyframes";
  style.textContent = `
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(40px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes gridPulse {
      0%, 100% { opacity: 0.03; }
      50%      { opacity: 0.07; }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50%      { transform: translateY(-20px); }
    }
    @keyframes blobMove1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(80px, -60px) scale(1.1); }
      50% { transform: translate(-40px, 40px) scale(0.95); }
      75% { transform: translate(60px, 20px) scale(1.05); }
    }
    @keyframes blobMove2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(-60px, 50px) scale(1.05); }
      50% { transform: translate(50px, -30px) scale(1.1); }
      75% { transform: translate(-20px, -40px) scale(0.95); }
    }
    @keyframes blobMove3 {
      0%, 100% { transform: translate(0, 0) scale(1.05); }
      33% { transform: translate(40px, 60px) scale(0.95); }
      66% { transform: translate(-60px, -20px) scale(1.1); }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes grain {
      0%, 100% { transform: translate(0, 0); }
      10% { transform: translate(-5%, -10%); }
      20% { transform: translate(-15%, 5%); }
      30% { transform: translate(7%, -25%); }
      40% { transform: translate(-5%, 25%); }
      50% { transform: translate(-15%, 10%); }
      60% { transform: translate(15%, 0%); }
      70% { transform: translate(0%, 15%); }
      80% { transform: translate(3%, 35%); }
      90% { transform: translate(-10%, 10%); }
    }
    .karela-nav-link {
      color: #1a1a2e;
      text-decoration: none;
      font-size: 0.9375rem;
      font-weight: 500;
      opacity: 0.7;
      transition: opacity 0.2s;
    }
    .karela-nav-link:hover { opacity: 1; }
    .karela-product-card {
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .karela-product-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.08);
    }
    .karela-cta-btn {
      display: inline-block;
      padding: 1rem 2.25rem;
      border: 1px solid #1a1a2e;
      border-radius: 0.5rem;
      background: transparent;
      color: #1a1a2e;
      text-decoration: none;
      font-weight: 600;
      font-size: 0.9375rem;
      letter-spacing: 0.01em;
      transition: background 0.25s, color 0.25s;
      cursor: pointer;
    }
    .karela-cta-btn:hover {
      background: #1a1a2e;
      color: #fff;
    }
    .karela-cta-btn-primary {
      display: inline-block;
      padding: 1rem 2.25rem;
      border: none;
      border-radius: 0.5rem;
      background: #1a1a2e;
      color: #fff;
      text-decoration: none;
      font-weight: 600;
      font-size: 0.9375rem;
      letter-spacing: 0.01em;
      transition: background 0.25s, transform 0.2s;
      cursor: pointer;
    }
    .karela-cta-btn-primary:hover {
      background: #2d2d4e;
      transform: translateY(-2px);
    }
  `;
  document.head.appendChild(style);
};

export function HomePage() {
  const [isMobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    injectKeyframes();
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!document.getElementById("google-font-inter")) {
      const link = document.createElement("link");
      link.id = "google-font-inter";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ─── Design Tokens ─────────────────────────────────────────── */
  const font = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
  const colors = {
    bg: "#fafafa",
    surface: "#ffffff",
    text: "#1a1a2e",
    textSecondary: "rgba(26,26,46,0.6)",
    textTertiary: "rgba(26,26,46,0.45)",
    border: "rgba(26,26,46,0.08)",
    accent: "#1a1a2e",
  };

  const sectionPadding = isMobile ? "5rem 1.5rem" : "8rem 4rem";

  return (
    <div
      style={{
        fontFamily: font,
        minHeight: "100vh",
        background: colors.bg,
        color: colors.text,
        overflowX: "hidden",
      }}
    >
      {/* ══════════════════ NAVIGATION ══════════════════ */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: isMobile ? "1rem 1.5rem" : "1.25rem 3rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: scrollY > 50 ? "rgba(250,250,250,0.85)" : "transparent",
          backdropFilter: scrollY > 50 ? "blur(20px)" : "none",
          borderBottom:
            scrollY > 50
              ? `1px solid ${colors.border}`
              : "1px solid transparent",
          zIndex: 100,
          transition: "all 0.3s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <img
            src="logo-dark.png"
            alt="Karela Technologies"
            style={{ height: isMobile ? "28px" : "34px", width: "auto" }}
          />
          <span
            style={{
              fontSize: isMobile ? "0.95rem" : "1.1rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: colors.text,
            }}
          >
            Karela Technologies Inc.
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: isMobile ? "1rem" : "2rem",
            alignItems: "center",
          }}
        >
          {!isMobile && (
            <>
              <a href="#products" className="karela-nav-link">
                Products
              </a>
              <a href="#about" className="karela-nav-link">
                About
              </a>
            </>
          )}
          <Link to="/blog" className="karela-nav-link">
            Blog
          </Link>
          <a
            href="mailto:contact@karelatechnologies.com"
            className="karela-cta-btn"
            style={{ padding: "0.5rem 1.25rem", fontSize: "0.875rem" }}
          >
            Get Started
          </a>
        </div>
      </nav>

      {/* ══════════════════ HERO ══════════════════ */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #f8f9fc 0%, #eef1f8 30%, #f0edf7 60%, #fafafa 100%)",
        }}
      >
        {/* Animated gradient blobs */}
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-5%",
            width: isMobile ? "350px" : "600px",
            height: isMobile ? "350px" : "600px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.04) 50%, transparent 70%)",
            filter: "blur(40px)",
            animation: "blobMove1 20s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-8%",
            width: isMobile ? "300px" : "500px",
            height: isMobile ? "300px" : "500px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.06) 0%, rgba(96,165,250,0.03) 50%, transparent 70%)",
            filter: "blur(40px)",
            animation: "blobMove2 25s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: isMobile ? "250px" : "400px",
            height: isMobile ? "250px" : "400px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(168,85,247,0.05) 0%, transparent 60%)",
            filter: "blur(50px)",
            animation: "blobMove3 18s ease-in-out infinite",
          }}
        />
        {/* Subtle grid overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(26,26,46,0.025) 1px, transparent 1px),
              linear-gradient(90deg, rgba(26,26,46,0.025) 1px, transparent 1px)
            `,
            backgroundSize: isMobile ? "40px 40px" : "80px 80px",
          }}
        />
        {/* Film grain texture */}
        <div
          style={{
            position: "absolute",
            inset: "-50%",
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
            backgroundSize: "256px 256px",
            animation: "grain 8s steps(10) infinite",
            pointerEvents: "none",
            opacity: 0.5,
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1100,
            padding: isMobile ? "8rem 1.5rem 4rem" : "0 4rem",
            animation: "fadeUp 1s ease-out",
          }}
        >
          {/* Arkios Enterprise badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.5rem 1.25rem",
              background: "rgba(26,26,46,0.04)",
              border: "1px solid rgba(26,26,46,0.08)",
              borderRadius: "2rem",
              marginBottom: "2rem",
              backdropFilter: "blur(10px)",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#22c55e",
                boxShadow: "0 0 6px rgba(34,197,94,0.4)",
              }}
            />
            <span
              style={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: colors.text,
                letterSpacing: "0.02em",
              }}
            >
              Arkios Enterprise is now available
            </span>
            <a
              href="https://arkios.ai"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: colors.text,
                textDecoration: "none",
                opacity: 0.6,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = "1")}
              onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
            >
              Visit &rarr;
            </a>
          </div>

          <h1
            style={{
              fontFamily: font,
              fontSize: isMobile ? "2.75rem" : "5rem",
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              margin: "0 0 2rem",
              maxWidth: 900,
              color: colors.text,
            }}
          >
            We build Software
            <br />
            Infrastructure for{" "}
            <span style={{ color: "rgba(26,26,46,0.35)" }}>the AI era.</span>
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1.125rem" : "1.375rem",
              lineHeight: 1.6,
              color: colors.textSecondary,
              maxWidth: 600,
              margin: "0 0 3rem",
            }}
          >
            From enterprise AI platforms to silicon-level tooling, we deliver
            the foundational infrastructure that powers the next generation of
            intelligent systems.
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <a href="#products" className="karela-cta-btn-primary">
              Explore Products
            </a>
            <a
              href="mailto:contact@karelatechnologies.com"
              className="karela-cta-btn"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════ OUR SOFTWARE ══════════════════ */}
      <section
        id="products"
        style={{
          padding: sectionPadding,
          maxWidth: 1300,
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "1.75rem" : "2.25rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            marginBottom: isMobile ? "3rem" : "5rem",
            color: colors.text,
          }}
        >
          Our Software
        </h2>

        {/* ── Product 1: Arkios AI ── */}
        <div
          className="karela-product-card"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "280px 1fr auto",
            alignItems: "start",
            gap: isMobile ? "1.5rem" : "3rem",
            padding: isMobile ? "2.5rem 1.5rem" : "4rem",
            background: colors.surface,
            borderRadius: "1.25rem",
            border: `1px solid ${colors.border}`,
            marginBottom: "2rem",
          }}
        >
          <div>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.125rem",
                lineHeight: 1.5,
                color: colors.textSecondary,
                margin: "0 0 2rem",
              }}
            >
              Complete AI infrastructure for modern enterprises
            </p>
            <span
              style={{
                fontSize: "0.8125rem",
                color: colors.textTertiary,
                fontWeight: 500,
              }}
            >
              /0.1
            </span>
          </div>

          {/* Decorative icon area */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: isMobile ? 120 : 180,
              background: "rgba(26,26,46,0.02)",
              borderRadius: "1rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <svg
              width={isMobile ? "80" : "120"}
              height={isMobile ? "80" : "120"}
              viewBox="0 0 120 120"
              fill="none"
              style={{ opacity: 0.15 }}
            >
              <rect
                x="10"
                y="10"
                width="40"
                height="40"
                rx="8"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="70"
                y="10"
                width="40"
                height="40"
                rx="8"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="10"
                y="70"
                width="40"
                height="40"
                rx="8"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="70"
                y="70"
                width="40"
                height="40"
                rx="8"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="50"
                y1="30"
                x2="70"
                y2="30"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="30"
                y1="50"
                x2="30"
                y2="70"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="90"
                y1="50"
                x2="90"
                y2="70"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="50"
                y1="90"
                x2="70"
                y2="90"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
            </svg>
          </div>

          {/* Product Name */}
          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <h3
              style={{
                fontSize: isMobile ? "3rem" : "5rem",
                fontWeight: 800,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                margin: 0,
                color: colors.text,
              }}
            >
              Arkios
            </h3>
            <span
              style={{
                fontSize: isMobile ? "1.25rem" : "1.5rem",
                fontWeight: 300,
                color: colors.textTertiary,
                letterSpacing: "-0.01em",
              }}
            >
              AI
            </span>
            <div style={{ marginTop: "1.5rem" }}>
              <a
                href="https://arkios.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="karela-cta-btn"
                style={{ padding: "0.625rem 1.5rem", fontSize: "0.8125rem" }}
              >
                Learn More →
              </a>
            </div>
          </div>
        </div>

        {/* ── Product 2: Arkios Code ── */}
        <div
          className="karela-product-card"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "280px 1fr auto",
            alignItems: "start",
            gap: isMobile ? "1.5rem" : "3rem",
            padding: isMobile ? "2.5rem 1.5rem" : "4rem",
            background: colors.surface,
            borderRadius: "1.25rem",
            border: `1px solid ${colors.border}`,
            marginBottom: "2rem",
          }}
        >
          <div>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.125rem",
                lineHeight: 1.5,
                color: colors.textSecondary,
                margin: "0 0 2rem",
              }}
            >
              AI infrastructure for silicon firmware and chips. A
              custom-tailored IDE for firmware engineers.
            </p>
            <span
              style={{
                fontSize: "0.8125rem",
                color: colors.textTertiary,
                fontWeight: 500,
              }}
            >
              /0.2
            </span>
          </div>

          {/* Decorative icon area */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: isMobile ? 120 : 180,
              background: "rgba(26,26,46,0.02)",
              borderRadius: "1rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <svg
              width={isMobile ? "80" : "120"}
              height={isMobile ? "80" : "120"}
              viewBox="0 0 120 120"
              fill="none"
              style={{ opacity: 0.15 }}
            >
              <rect
                x="20"
                y="15"
                width="80"
                height="55"
                rx="6"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="30"
                y1="35"
                x2="55"
                y2="35"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="30"
                y1="45"
                x2="70"
                y2="45"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="30"
                y1="55"
                x2="50"
                y2="55"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="25"
                y="80"
                width="20"
                height="25"
                rx="3"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="50"
                y="80"
                width="20"
                height="25"
                rx="3"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <rect
                x="75"
                y="80"
                width="20"
                height="25"
                rx="3"
                stroke="#1a1a2e"
                strokeWidth="2"
              />
              <line
                x1="35"
                y1="70"
                x2="35"
                y2="80"
                stroke="#1a1a2e"
                strokeWidth="1.5"
              />
              <line
                x1="60"
                y1="70"
                x2="60"
                y2="80"
                stroke="#1a1a2e"
                strokeWidth="1.5"
              />
              <line
                x1="85"
                y1="70"
                x2="85"
                y2="80"
                stroke="#1a1a2e"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          {/* Product Name */}
          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <h3
              style={{
                fontSize: isMobile ? "3rem" : "5rem",
                fontWeight: 800,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                margin: 0,
                color: colors.text,
              }}
            >
              Arkios
            </h3>
            <span
              style={{
                fontSize: isMobile ? "1.25rem" : "1.5rem",
                fontWeight: 300,
                color: colors.textTertiary,
                letterSpacing: "-0.01em",
              }}
            >
              Code
            </span>
            <div style={{ marginTop: "1.5rem" }}>
              <a
                href="https://code.arkios.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="karela-cta-btn"
                style={{ padding: "0.625rem 1.5rem", fontSize: "0.8125rem" }}
              >
                Beta Testing Available →
              </a>
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: colors.textTertiary,
                  marginTop: "0.75rem",
                  marginBottom: 0,
                }}
              >
                code.arkios.ai
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ WHAT WE BELIEVE ══════════════════ */}
      <section
        id="about"
        style={{
          padding: isMobile ? "6rem 1.5rem" : "10rem 4rem",
          background: colors.surface,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <h2
            style={{
              fontSize: isMobile ? "2.5rem" : "4.5rem",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.08,
              maxWidth: 1100,
              margin: "0 0 3rem",
              color: colors.text,
            }}
          >
            Our software powers real-time,{" "}
            <span style={{ color: "rgba(26,26,46,0.3)" }}>AI-driven</span>{" "}
            decisions across enterprises and silicon manufacturers, from the
            cloud to the chip.
          </h2>
          <p
            style={{
              fontSize: isMobile ? "1.25rem" : "1.5rem",
              lineHeight: 1.6,
              color: colors.textSecondary,
              maxWidth: 700,
              margin: 0,
            }}
          >
            We believe the AI revolution needs more than models. It needs
            robust, scalable infrastructure at every layer of the stack. From
            cloud to chip, Karela Technologies is building the tools that will
            power the next decade of intelligent computing.
          </p>
        </div>
      </section>

      {/* ══════════════════ COMPANY / ABOUT ══════════════════ */}
      <section
        style={{
          padding: sectionPadding,
          maxWidth: 1300,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "3rem" : "6rem",
          }}
        >
          {/* Left */}
          <div>
            <p
              style={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: colors.textTertiary,
                marginBottom: "1.5rem",
              }}
            >
              About the Company
            </p>
            <h2
              style={{
                fontSize: isMobile ? "2rem" : "2.75rem",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
                margin: "0 0 1.5rem",
                color: colors.text,
              }}
            >
              We are Karela Technologies
            </h2>
            <p
              style={{
                fontSize: isMobile ? "1.05rem" : "1.125rem",
                lineHeight: 1.7,
                color: colors.textSecondary,
                margin: "0 0 1.5rem",
              }}
            >
              Founded with the conviction that AI infrastructure should be as
              reliable as the internet itself, Karela Technologies builds
              software that powers real-time, AI-driven decisions across
              industries, from enterprise operations to silicon manufacturing.
            </p>
            <p
              style={{
                fontSize: isMobile ? "1.05rem" : "1.125rem",
                lineHeight: 1.7,
                color: colors.textSecondary,
                margin: 0,
              }}
            >
              We believe in building products that are robust, scalable, and
              human-centered. Our teams work at the intersection of AI research
              and production engineering, delivering tools that move from
              prototype to production in record time.
            </p>
          </div>

          {/* Right - Details */}
          <div>
            <div style={{ marginBottom: "3rem" }}>
              <h3
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: colors.textTertiary,
                  marginBottom: "0.75rem",
                }}
              >
                Headquarters
              </h3>
              <p
                style={{
                  fontSize: "1.125rem",
                  fontWeight: 500,
                  color: colors.text,
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                Mesa, Arizona
                <br />
                <span style={{ color: colors.textSecondary, fontWeight: 400 }}>
                  United States
                </span>
              </p>
            </div>

            <div style={{ marginBottom: "3rem" }}>
              <h3
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: colors.textTertiary,
                  marginBottom: "0.75rem",
                }}
              >
                Focus Areas
              </h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {[
                  "AI Infrastructure",
                  "Enterprise AI",
                  "Firmware Tooling",
                  "Developer Tools",
                  "Autonomous Agents",
                  "Silicon Software",
                ].map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: "0.5rem 1rem",
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: "2rem",
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: "3rem" }}>
              <h3
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: colors.textTertiary,
                  marginBottom: "0.75rem",
                }}
              >
                Contact
              </h3>
              <a
                href="mailto:contact@karelatechnologies.com"
                style={{
                  fontSize: "1.125rem",
                  color: colors.text,
                  textDecoration: "none",
                  borderBottom: `1px solid ${colors.border}`,
                  paddingBottom: "0.125rem",
                  transition: "border-color 0.2s",
                }}
              >
                contact@karelatechnologies.com
              </a>
            </div>

            <div>
              <h3
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: colors.textTertiary,
                  marginBottom: "0.75rem",
                }}
              >
                Careers
              </h3>
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  color: colors.textSecondary,
                  margin: "0 0 1rem",
                }}
              >
                We're looking for exceptional engineers and problem solvers who
                want to build foundational AI infrastructure.
              </p>
              <a
                href="mailto:contact@karelatechnologies.com"
                className="karela-cta-btn"
                style={{ padding: "0.625rem 1.5rem", fontSize: "0.8125rem" }}
              >
                Open Positions →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ FOOTER ══════════════════ */}
      <footer
        style={{
          padding: isMobile ? "3rem 1.5rem" : "3.5rem 4rem",
          borderTop: `1px solid ${colors.border}`,
          background: colors.bg,
        }}
      >
        <div
          style={{
            maxWidth: 1300,
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: "1.5rem",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <img
              src="logo-dark.png"
              alt="Karela Technologies"
              style={{ height: "24px", width: "auto", opacity: 0.6 }}
            />
            <span
              style={{
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: colors.text,
              }}
            >
              Karela Technologies Inc.
            </span>
          </div>
          <div
            style={{
              display: "flex",
              gap: isMobile ? "1rem" : "2rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "0.8125rem", color: colors.textTertiary }}>
              Mesa, Arizona
            </span>
            <Link
              to="/blog"
              style={{
                fontSize: "0.8125rem",
                color: colors.textSecondary,
                textDecoration: "none",
              }}
            >
              Blog
            </Link>
            <a
              href="mailto:contact@karelatechnologies.com"
              style={{
                fontSize: "0.8125rem",
                color: colors.textSecondary,
                textDecoration: "none",
              }}
            >
              Contact
            </a>
          </div>
          <span
            style={{
              fontSize: "0.75rem",
              color: colors.textTertiary,
            }}
          >
            © {new Date().getFullYear()} Karela Technologies Inc. All rights
            reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
